import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './ArchiveStyles.css';
import { ARCHIVE_PROFILES, ARCHIVE_PROFILE_IMPORT_FILE, ARCHIVE_PROFILE_EXPORT_FILE } from '../../auth/urlParameters';
import { Api } from '../../auth/Configs';
import { ToastError, ToastConfirm } from '../../../ui/AlertesUi';
import { SimpleSpinner } from '../../../ui/Spinner';

const statusLabels = {
  draft: 'Brouillon',
  active: 'Actif',
  retired: 'Retiré',
};

const flattenServices = (nodes, depth = 0) =>
  (nodes || []).reduce((result, service) => {
    result.push({ service, depth });
    if (service.children?.length) {
      result.push(...flattenServices(service.children, depth + 1));
    }
    return result;
  }, []);

const GetProfileListe = () => {
  const session = useSelector((state) => state.sessionAuth);
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [filters, setFilters] = useState({ q: '', status: '', confidentialite: '', producerServiceId: '' });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const jsonInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const pageSelectRef = useRef(null);

  const canManage = useMemo(() => {
    const roles = Array.isArray(session.roles) ? session.roles : [];
    return ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].some((role) => roles.includes(role));
  }, [session.roles]);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const fetchProfiles = useCallback(() => {
    setLoading(true);
    axios
      .get(ARCHIVE_PROFILES(), {
        ...authHeaders,
        params: { ...filters, page, limit: 20 },
      })
      .then((res) => {
        setProfiles(res.data?.profiles || []);
        setTotal(res.data?.count || 0);
        setPages(res.data?.pagination?.pages || 1);
      })
      .catch(() => ToastError("Impossible de charger les profils d'archive"))
      .finally(() => setLoading(false));
  }, [authHeaders, filters, page]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    axios
      .get(`${Api}/services`, authHeaders)
      .then((res) => setServices(flattenServices(res.data?.services || [])))
      .catch(() => setServices([]));
  }, [authHeaders]);

  useEffect(() => {
    if (!pageSelectRef.current) return;
    const selectedOnPage = profiles.filter((profile) => selectedIds.includes(profile.id)).length;
    pageSelectRef.current.indeterminate = selectedOnPage > 0 && selectedOnPage < profiles.length;
  }, [profiles, selectedIds]);

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value.substring(0, 10) : date.toLocaleDateString('fr-FR');
  };

  const updateFilter = (name, value) => {
    setPage(1);
    setSelectedIds([]);
    setFilters((previous) => ({ ...previous, [name]: value }));
  };

  const toggleProfile = (id) => {
    if (!selectedIds.includes(id) && selectedIds.length >= 100) { ToastError('Sélection limitée à 100 profils.'); return; }
    setSelectedIds((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id]);
  };

  const togglePage = () => {
    const pageIds = profiles.map((profile) => profile.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    if (!allSelected && new Set([...selectedIds, ...pageIds]).size > 100) { ToastError('Sélection limitée à 100 profils.'); return; }
    setSelectedIds((current) => allSelected
      ? current.filter((id) => !pageIds.includes(id))
      : [...new Set([...current, ...pageIds])]);
  };

  const exportProfiles = async (format) => {
    if (!selectedIds.length) return;
    setExporting(true);
    try {
      const response = await axios.post(ARCHIVE_PROFILE_EXPORT_FILE(), { ids: selectedIds, format }, { ...authHeaders, responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.headers['content-disposition']?.match(/filename="?([^";]+)/)?.[1] || `profils-archives.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      ToastError("Impossible d'exporter les profils sélectionnés.");
    } finally {
      setExporting(false);
    }
  };

  const importProfiles = async (event, format) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      ToastError('Le fichier ne doit pas dépasser 2 Mo.');
      event.target.value = '';
      return;
    }
    const data = new FormData();
    data.append('file', file);
    data.append('format', format);
    setImporting(true);
    try {
      const response = await axios.post(ARCHIVE_PROFILE_IMPORT_FILE(), data, authHeaders);
      const { created = 0 } = response.data || {};
      ToastConfirm(`${created} profil(s) importé(s).`);
      if (page !== 1) setPage(1);
      else fetchProfiles();
    } catch (error) {
      ToastError(error.response?.data?.message || "Impossible d'importer les profils.");
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Archives" submenu="Profils d'archive" />
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-5 col-lg-4 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div className="col-md-7 col-lg-8 col-xl-9 profile-list-page">
              <div className="profile-list-header">
                <div>
                  <h4 className="profile-list-title">Profils d'archive</h4>
                  <p className="profile-list-subtitle">
                    Gérez les profils et leurs règles de classement.
                    <span className="profile-list-count">{total}</span>
                  </p>
                </div>
                <div className="profile-list-actions">
                  {canManage && <>
                    <input
                      ref={jsonInputRef}
                      type="file"
                      accept=".json,application/json"
                      className="d-none"
                      aria-label="Fichier JSON de profils d'archive"
                      onChange={(event) => importProfiles(event, 'json')}
                    />
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="d-none"
                      aria-label="Fichier CSV de profils d'archive"
                      onChange={(event) => importProfiles(event, 'csv')}
                    />
                    <button
                      className="btn btn-outline-primary btn-file-action"
                      type="button"
                      aria-label="Importer des profils depuis un fichier JSON"
                      disabled={importing}
                      onClick={() => jsonInputRef.current?.click()}
                    >
                      <i className="ri-upload-2-line" aria-hidden="true" />
                      <span>{importing ? 'Importation…' : 'Importer JSON'}</span>
                    </button>
                    <button
                      className="btn btn-outline-primary btn-file-action"
                      type="button"
                      aria-label="Importer des profils depuis un fichier CSV"
                      disabled={importing}
                      onClick={() => csvInputRef.current?.click()}
                    >
                      <i className="ri-upload-2-line" aria-hidden="true" />
                      <span>Importer CSV</span>
                    </button>
                    <button
                      className="btn profile-primary-button btn-uniform-action"
                      onClick={() => navigate('/apps/in/archives/profils/add')}
                    >
                      <i className="ri-add-circle-line" aria-hidden="true" /> Créer un profil
                    </button>
                  </>}
                </div>
              </div>

              {canManage && <details className="mb-3">
                <summary>Format des fichiers d’import</summary>
                <p className="mb-1">JSON : un profil, une liste de profils ou un objet avec une propriété <code>profiles</code>. CSV : séparateur point-virgule, colonnes <code>code;title;documentType;confidentialite;status;captureFields</code>.</p>
                <p className="mb-0">Les colonnes <code>code</code> et <code>title</code> sont obligatoires. Dans le CSV, <code>captureFields</code> contient un tableau JSON. Les codes déjà présents sont refusés ; le lot entier est contrôlé avant création.</p>
              </details>}

              <div className="profile-list-filters">
                <input
                  className="form-control"
                  placeholder="Rechercher par code ou titre"
                  value={filters.q}
                  onChange={(event) => updateFilter('q', event.target.value)}
                />
                <select
                  className="form-control"
                  value={filters.status}
                  onChange={(event) => updateFilter('status', event.target.value)}
                >
                  <option value="">Tous les statuts</option>
                  <option value="draft">Brouillon</option>
                  <option value="active">Actif</option>
                  <option value="retired">Retiré</option>
                </select>
                <select
                  className="form-control"
                  value={filters.confidentialite}
                  onChange={(event) => updateFilter('confidentialite', event.target.value)}
                >
                  <option value="">Toutes les confidentialités</option>
                  <option value="Interne">Interne</option>
                  <option value="Restreint">Restreint</option>
                  <option value="Confidentiel">Confidentiel</option>
                  <option value="Très Confidentiel">Très Confidentiel</option>
                </select>
                <select
                  className="form-control"
                  value={filters.producerServiceId}
                  onChange={(event) => updateFilter('producerServiceId', event.target.value)}
                >
                  <option value="">Tous les services</option>
                  {services.map(({ service, depth }) => (
                    <option key={service.id} value={service.id}>
                      {'— '.repeat(depth)}{service.code} — {service.title}
                    </option>
                  ))}
                </select>
              </div>

              {canManage && <div className="profile-selection-actions" aria-live="polite">
                <span>{selectedIds.length} profil(s) sélectionné(s)</span>
                <button type="button" className="btn btn-outline-primary btn-file-action" disabled={!selectedIds.length || exporting} onClick={() => exportProfiles('json')}>Exporter JSON</button>
                <button type="button" className="btn btn-outline-primary btn-file-action" disabled={!selectedIds.length || exporting} onClick={() => exportProfiles('csv')}>Exporter CSV</button>
                {selectedIds.length > 0 && <button type="button" className="btn btn-link" onClick={() => setSelectedIds([])}>Effacer la sélection</button>}
              </div>}

              <div className="row profile-list-content">
                <div className="col-12">
                  <div className="card card-table profile-list-card mb-0">
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-hover table-center profile-list-table syidocs-data-table mb-0">
                          <thead>
                            <tr>
                              {canManage && <th className="profile-select-cell"><input ref={pageSelectRef} type="checkbox" aria-label="Sélectionner les profils de cette page" checked={profiles.length > 0 && profiles.every((profile) => selectedIds.includes(profile.id))} onChange={togglePage} disabled={loading || profiles.length === 0} /></th>}
                              <th>Code / Titre</th>
                              <th>Service producteur</th>
                              <th>Confidentialité</th>
                              <th>Statut</th>
                              <th>Créé le</th>
                              <th className="profile-actions-column">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan={canManage ? 7 : 6}>
                                  <div className="col-12 item simpleSpinner">
                                    {SimpleSpinner()}
                                    <span>Chargement...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : profiles.length === 0 ? (
                              <tr>
                                <td colSpan={canManage ? 7 : 6}>
                                  <div className="alert alert-info mb-0">Aucun profil disponible.</div>
                                </td>
                              </tr>
                            ) : (
                              profiles.map((profile) => (
                                <tr key={profile.id}>
                                  {canManage && <td className="profile-select-cell"><input type="checkbox" aria-label={`Sélectionner ${profile.code || profile.title}`} checked={selectedIds.includes(profile.id)} onChange={() => toggleProfile(profile.id)} /></td>}
                                  <td className="profile-title-cell">
                                    <div className="profile-identity">
                                      <span className="profile-code">{profile.code || '—'}</span>
                                      <span className="profile-identity-separator" aria-hidden="true">—</span>
                                      <span className="profile-title">{profile.title || 'Sans intitulé'}</span>
                                    </div>
                                  </td>
                                  <td>
                                    {profile.producerService
                                      ? `${profile.producerService.code} — ${profile.producerService.title}`
                                      : profile.producer || 'Non défini'}
                                  </td>
                                  <td>
                                    <span className="profile-confidentiality">
                                      {profile.confidentialite || 'Non défini'}
                                    </span>
                                  </td>
                                  <td>
                                    {statusLabels[profile.status] || 'Brouillon'} · v{profile.version || 1}
                                  </td>
                                  <td><time dateTime={profile.createdAt || ''}>{formatDate(profile.createdAt) || '-'}</time></td>
                                  <td className="text-right profile-action-cell">
                                    <div className="table-action">
                                      <button
                                        className="syidocs-table-icon-button"
                                        type="button"
                                        aria-label={`${canManage ? 'Éditer' : 'Consulter'} ${profile.title || 'le profil'}`}
                                        title={canManage ? 'Éditer' : 'Consulter'}
                                        onClick={() => navigate(`/apps/in/archives/profils/${profile.id}`)}
                                      >
                                        <i className={canManage ? 'ri-pencil-line' : 'ri-eye-line'} aria-hidden="true" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  {pages > 1 && (
                    <div className="profile-pagination">
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        disabled={page <= 1}
                        onClick={() => setPage((value) => Math.max(1, value - 1))}
                      >
                        Précédent
                      </button>
                      <span>Page {page} sur {pages}</span>
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        disabled={page >= pages}
                        onClick={() => setPage((value) => Math.min(pages, value + 1))}
                      >
                        Suivant
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default GetProfileListe;
